import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../mock';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = services.find(s => s.id === serviceId);

  if (!service) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl font-bold text-slate-900 mb-4">Service Not Found</h1>
        <Link to="/services">
          <Button>Back to Services</Button>
        </Link>
      </div>
    );
  }

  const specialistDetails = {
    'house-cleaning': {
      approach: 'Our residential cleaning approach combines thoroughness with care. We understand that your home is your sanctuary, and we treat it with the respect it deserves. Using eco-friendly products and proven techniques, we ensure every surface is spotless while maintaining a safe environment for your family and pets.',
      duration: '2-4 hours depending on home size',
      results: 'A fresh, welcoming home with sparkling surfaces, clean floors, and sanitized bathrooms and kitchen. You\'ll notice the difference in air quality and overall ambiance.',
      safety: 'All products are eco-friendly, non-toxic, and safe for children and pets. Our team follows strict protocols to ensure your belongings are handled with care.'
    },
    'commercial-cleaning': {
      approach: 'Commercial spaces require a different level of attention. We work efficiently to minimize disruption while delivering impeccable results. Our team is trained in commercial cleaning standards and understands the importance of maintaining a professional environment that impresses clients and supports staff productivity.',
      duration: '1-3 hours depending on facility size and frequency',
      results: 'A pristine, professional workspace with clean floors, sanitized restrooms, dust-free surfaces, and a welcoming reception area. Your clients and employees will notice the difference.',
      safety: 'We use commercial-grade, EPA-approved cleaning products. All cleaning is done following health and safety regulations, with special attention to high-touch surfaces.'
    },
    'specialist-cleaning': {
      approach: 'Specialist cleaning requires expertise and specialized equipment. Whether it\'s deep carpet cleaning, post-construction cleanup, or appliance deep cleaning, we use professional-grade tools and techniques. Our gentle but thorough approach ensures outstanding results without damaging delicate surfaces.',
      duration: 'Varies by service: Carpet cleaning 2-4 hours, Post-construction 4-8 hours, Oven/Fridge 1-2 hours',
      results: 'Carpets look and smell fresh with deep-set stains removed. Post-construction spaces are dust-free and move-in ready. Ovens and fridges are sparkling inside and out, functioning more efficiently.',
      safety: 'We use specialized equipment with safety guards and follow manufacturer guidelines. For post-construction, we wear protective gear and ensure proper ventilation. All products are suitable for food-contact surfaces where applicable.'
    }
  };

  const details = specialistDetails[serviceId];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-[400px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.85), rgba(224, 242, 254, 0.9)), url('${service.image}?w=1200&q=70&auto=format')`,
          backgroundBlendMode: 'overlay'
        }}
      >
        <div className="container mx-auto px-4">
          <Link to="/services" className="inline-flex items-center text-sky-600 hover:text-sky-700 mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Services
          </Link>
          <h1 className="text-5xl font-bold text-slate-900 mb-4">{service.title}</h1>
          <p className="text-xl text-slate-600 max-w-3xl">{service.description}</p>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="border-sky-100">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-gradient-to-br from-sky-100 to-emerald-100 p-3 rounded-lg">
                      <CheckCircle className="h-6 w-6 text-sky-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-2">What's Included</h3>
                      <ul className="space-y-2">
                        {service.tasks.map((task, idx) => (
                          <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                            <span className="text-emerald-600 mt-1">•</span>
                            <span>{task}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-sky-100">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-gradient-to-br from-sky-100 to-emerald-100 p-3 rounded-lg">
                      <Clock className="h-6 w-6 text-sky-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-2">Typical Duration</h3>
                      <p className="text-sm text-slate-600">{details.duration}</p>
                      <div className="mt-4">
                        <h4 className="text-sm font-semibold text-slate-800 mb-1">Ideal For:</h4>
                        <p className="text-sm text-slate-600">{service.idealFor}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Approach Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Approach</h2>
              <p className="text-slate-600 text-lg leading-relaxed">{details.approach}</p>
            </div>

            {/* Results Section */}
            <div className="bg-gradient-to-br from-sky-50 to-emerald-50 rounded-lg p-8 mb-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Results You'll Notice</h3>
              <p className="text-slate-700 leading-relaxed">{details.results}</p>
            </div>

            {/* Safety Section */}
            <Card className="border-sky-100 mb-12">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-sky-100 to-emerald-100 p-3 rounded-lg flex-shrink-0">
                    <Shield className="h-6 w-6 text-sky-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800 mb-3">Safety & Care</h3>
                    <p className="text-slate-600">{details.safety}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA Section */}
            <div className="text-center bg-sky-600 rounded-lg p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="mb-6 text-sky-100">Request a free quote and we'll respond within 24-48 hours with a customized plan.</p>
              <Link to="/contact">
                <Button size="lg" variant="secondary" className="bg-white text-sky-600 hover:bg-sky-50">
                  Request a Quote <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
